/**
 * Badge System for Golden Quiz
 *
 * Awards badges for various achievements:
 * - Category mastery (complete all difficulties)
 * - Perfect scores
 * - Streaks
 * - Total quizzes completed
 */

import { getCategoryStats, hasCategoryMastery, getTotalQuizzesCompleted, getCurrentStreak, getCategoryDifficultyHistory } from './quizHistory';

export const BADGE_DEFINITIONS = {
  // Category Mastery Badges
  CATEGORY_MASTER: {
    id: 'category-master',
    name: 'Category Master',
    description: 'Complete all difficulty levels in a category',
    icon: '🏆',
    color: '#FFD700',
    checkFunction: (categoryId) => hasCategoryMastery(categoryId)
  },

  PERFECT_SCORE: {
    id: 'perfect-score',
    name: 'Perfect Score',
    description: 'Score 100% on any quiz',
    icon: '💯',
    color: '#22C55E',
    checkFunction: (categoryId, difficulty) => {
      const history = getCategoryDifficultyHistory(categoryId, difficulty);
      return history.some(attempt => (attempt.score / attempt.totalQuestions) * 100 === 100);
    }
  },

  // Streak Badges
  WEEK_STREAK: {
    id: 'week-streak',
    name: '7-Day Streak',
    description: 'Complete quizzes for 7 days in a row',
    icon: '🔥',
    color: '#F59E0B',
    checkFunction: () => getCurrentStreak() >= 7
  },

  MONTH_STREAK: {
    id: 'month-streak',
    name: '30-Day Streak',
    description: 'Complete quizzes for 30 days in a row',
    icon: '🔥',
    color: '#EF4444',
    checkFunction: () => getCurrentStreak() >= 30
  },

  // Volume Badges
  BEGINNER: {
    id: 'beginner',
    name: 'Beginner',
    description: 'Complete 5 quizzes',
    icon: '🌱',
    color: '#22C55E',
    checkFunction: () => getTotalQuizzesCompleted() >= 5
  },

  APPRENTICE: {
    id: 'apprentice',
    name: 'Apprentice',
    description: 'Complete 25 quizzes',
    icon: '📚',
    color: '#3B82F6',
    checkFunction: () => getTotalQuizzesCompleted() >= 25
  },

  EXPERT: {
    id: 'expert',
    name: 'Expert',
    description: 'Complete 50 quizzes',
    icon: '🎓',
    color: '#8B5CF6',
    checkFunction: () => getTotalQuizzesCompleted() >= 50
  },

  MASTER: {
    id: 'master',
    name: 'Master',
    description: 'Complete 100 quizzes',
    icon: '👑',
    color: '#FFD700',
    checkFunction: () => getTotalQuizzesCompleted() >= 100
  },

  // Difficulty Badges
  HARD_MODE: {
    id: 'hard-mode',
    name: 'Hard Mode Warrior',
    description: 'Score 80%+ on a Hard difficulty quiz',
    icon: '⚔️',
    color: '#EF4444',
    checkFunction: (categoryId) => {
      const history = getCategoryDifficultyHistory(categoryId, 'hard');
      return history.some(attempt => (attempt.score / attempt.totalQuestions) * 100 >= 80);
    }
  },

  // All Categories Badge
  POLYMATH: {
    id: 'polymath',
    name: 'Polymath',
    description: 'Complete at least one quiz in every category',
    icon: '🌟',
    color: '#D4AF37',
    checkFunction: () => {
      const stats = getCategoryStats();
      const categories = Object.keys(stats);
      return categories.length >= 7; // All 7 categories
    }
  }
};

/**
 * Get all badges earned by user
 * @returns {Array} Array of earned badges
 */
export function getEarnedBadges() {
  const earnedBadges = [];
  const stats = getCategoryStats();

  for (const [key, badge] of Object.entries(BADGE_DEFINITIONS)) {
    // Check global badges (no category required)
    if (!badge.checkFunction.length || badge.checkFunction.length === 0) {
      if (badge.checkFunction()) {
        earnedBadges.push({
          ...badge,
          earnedAt: new Date().toISOString()
        });
      }
    } else {
      // Check category-specific badges
      for (const categoryId of Object.keys(stats)) {
        if (badge.checkFunction(categoryId)) {
          earnedBadges.push({
            ...badge,
            categoryId,
            earnedAt: new Date().toISOString()
          });
          break; // Only add once
        }
      }
    }
  }

  return earnedBadges;
}

/**
 * Get badges for a specific category
 * @param {string} categoryId - Category ID
 * @returns {Array} Badges earned in this category
 */
export function getCategoryBadges(categoryId) {
  const allBadges = getEarnedBadges();
  return allBadges.filter(badge =>
    badge.categoryId === categoryId || !badge.categoryId
  );
}

/**
 * Get next badge to earn (closest to completion)
 * @returns {Object|null} Next badge with progress
 */
export function getNextBadge() {
  const totalQuizzes = getTotalQuizzesCompleted();
  const streak = getCurrentStreak();

  const progressMap = [
    { badge: BADGE_DEFINITIONS.BEGINNER, current: totalQuizzes, target: 5 },
    { badge: BADGE_DEFINITIONS.APPRENTICE, current: totalQuizzes, target: 25 },
    { badge: BADGE_DEFINITIONS.EXPERT, current: totalQuizzes, target: 50 },
    { badge: BADGE_DEFINITIONS.MASTER, current: totalQuizzes, target: 100 },
    { badge: BADGE_DEFINITIONS.WEEK_STREAK, current: streak, target: 7 },
    { badge: BADGE_DEFINITIONS.MONTH_STREAK, current: streak, target: 30 }
  ];

  // Find next badge that's not yet earned
  const nextBadges = progressMap
    .filter(item => item.current < item.target)
    .sort((a, b) => {
      const aProgress = a.current / a.target;
      const bProgress = b.current / b.target;
      return bProgress - aProgress; // Sort by closest to completion
    });

  if (nextBadges.length > 0) {
    const next = nextBadges[0];
    return {
      ...next.badge,
      progress: next.current,
      target: next.target,
      percentage: Math.round((next.current / next.target) * 100)
    };
  }

  return null;
}

/**
 * Get total badge count
 * @returns {number} Number of badges earned
 */
export function getTotalBadgeCount() {
  return getEarnedBadges().length;
}

export default {
  BADGE_DEFINITIONS,
  getEarnedBadges,
  getCategoryBadges,
  getNextBadge,
  getTotalBadgeCount
};
